import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface UploadReceiptData {
  reservationId: string;
  file: File;
}

export const useUploadReservationReceipt = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reservationId, file }: UploadReceiptData) => {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Solo se permiten archivos de imagen (JPG, PNG, WEBP)');
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('El archivo no puede ser mayor a 5MB');
      }

      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${reservationId}/receipt_${Date.now()}.${fileExt}`;

      // Upload file to storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('reservation-receipts')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('reservation-receipts')
        .getPublicUrl(fileName);

      // Update reservation with receipt URL
      const { error: updateError } = await supabase
        .from('reservations')
        .update({ payment_receipt_url: publicUrl })
        .eq('id', reservationId);

      if (updateError) throw updateError;

      return { url: publicUrl, fileName };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['gym-reservations'] });
      toast.success("Comprobante subido exitosamente");
    },
    onError: (error) => {
      toast.error("Error al subir el comprobante: " + error.message);
    },
  });
};